/**
 * Step Detail - NetDocuments Integration Hooks
 * Provides OAuth authentication, token management, cabinet/document loading,
 * secure links, follow documents, workspace navigation, and browser viewing
 * functionality for NetDocuments integration.
 *
 * Authentication Flow:
 * 1. Opens popup to backend /NDApi/GetAuthorizationCode
 * 2. User authenticates with NetDocuments OAuth
 * 3. Backend exchanges code for tokens, returns via postMessage
 * 4. Tokens stored in localStorage as NDToken
 * 5. Token refresh handled automatically when expired
 */

import { useState, useCallback, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/api/apiInstance';
import { apiClient } from '@/api/client';
import { useNotificationStore } from '@/store/useNotificationStore';
import { useTranslation } from '@/i18n';
import { useNetDocumentsStore } from '@/store/useNetDocumentsStore';
import { useSettings } from '@/api/hooks/useSettings';
import { config } from '@/config/environment';
import type {
  MotrDocumentsGetProviderSecureLinkVm,
  MotrDocumentsModelsFollowDocumentsVm,
  MotrDocumentsModelsFollowDocumentViewModel,
  MotrDocumentsModelsDocumentRepoCabinetInfoVm,
} from '@/api/generated-sta/data-contracts';

import {
  getStoredNdToken,
  storeNdToken,
  clearNdToken,
  isNdTokenExpired,
  refreshNdToken,
  getValidNdAccessToken,
} from '@/utils/ndTokenManager';
import type { NDTokenData } from '@/utils/ndTokenManager';

// ============================================================================
// Constants
// ============================================================================

const PROVIDER_NAME = 'NetDocuments';


// Query keys for caching
export const netDocumentsKeys = {
  all: ['netDocuments'] as const,
  cabinets: () => [...netDocumentsKeys.all, 'cabinets'] as const,
  cabinetSettings: () => [...netDocumentsKeys.all, 'cabinetSettings'] as const,
  clients: (cabinetId: string, attributeId: string) =>
    [...netDocumentsKeys.all, 'clients', cabinetId, attributeId] as const,
  matters: (cabinetId: string, clientId: string, attributeId: string) =>
    [...netDocumentsKeys.all, 'matters', cabinetId, clientId, attributeId] as const,
  folders: (cabinetId: string) =>
    [...netDocumentsKeys.all, 'folders', cabinetId] as const,
  folderContent: (cabinetId: string, folderId?: string) =>
    [...netDocumentsKeys.all, 'folderContent', cabinetId, folderId] as const,
  workspace: (cabinetId: string, clientId?: string, matterId?: string) =>
    [...netDocumentsKeys.all, 'workspace', cabinetId, clientId, matterId] as const,
  workspaceData: (cabinetId: string, clientId: string, matterId: string) =>
    [...netDocumentsKeys.all, 'workspaceData', cabinetId, clientId, matterId] as const,
};

// ============================================================================
// Types
// ============================================================================

/** NetDocuments OAuth token structure (re-exported from ndTokenManager) */
export type NDToken = NDTokenData;

/** Cabinet info from API */
export interface NDCabinet {
  cabinetId: string;
  cabinetName: string;
  repositoryId?: string;
  envId?: string;
  firstAttributeId?: string;
  firstAttributeName?: string;
  secondAttributeId?: string;
  secondAttributeName?: string;
  thirdAttributeId?: string;
}

/** Client (tier 1 attribute) */
export interface NDClient {
  id: string;
  name: string;
  displayName?: string;
}

/** Matter (tier 2 attribute) */
export interface NDMatter {
  id: string;
  name: string;
  displayName?: string;
}

/** Document from NetDocuments */
export interface NDDocument {
  id: string;
  name: string;
  extension?: string;
  size?: number;
  created?: string;
  createdBy?: string;
  modified?: string;
  modifiedBy?: string;
  url?: string;
  envId?: string;
  version?: string;
}

/** Folder from NetDocuments */
export interface NDFolder {
  id: string;
  name: string;
  type: 'folder' | 'workspace';
}

/** Folder content response */
export interface NDFolderContent {
  folders: NDFolder[];
  documents: NDDocument[];
}

// Token utilities are now centralised in @/utils/ndTokenManager
// Re-export for backward compatibility with any external imports
export { getStoredNdToken, storeNdToken, clearNdToken, isNdTokenExpired };

// ============================================================================
// Authentication Hook
// ============================================================================

/**
 * Hook for managing NetDocuments OAuth authentication
 * Handles popup-based OAuth flow and token management
 */
export const useNetDocumentsAuth = () => {
  // Read auth state from the singleton store – no per-instance server calls
  const isAuthenticated = useNetDocumentsStore((s) => s.isAuthenticated);

  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const { addToast } = useNotificationStore();
  const { t } = useTranslation();

  // Get redirect URI from settings
  const { data: settingsData } = useSettings('motr.NetDocument.RedirectURI');

  /**
   * Refresh access token using refresh token.
   * Delegates to the shared ndTokenManager which deduplicates concurrent calls.
   */
  const refreshToken = useCallback(async (refreshTokenValue: string): Promise<boolean> => {
    const ok = await refreshNdToken(refreshTokenValue);
    useNetDocumentsStore.getState().setAuthenticated(ok);
    return ok;
  }, []);

  /**
   * Trigger NetDocuments OAuth authentication flow
   * Opens a popup window for OAuth authorization
   */
  const authenticate = useCallback(() => {
    setIsAuthenticating(true);
    setAuthError(null);

    // Get redirect URI from settings or use default
    let redirectUri = config.apiUrl;
    const ndRedirectSetting = Array.isArray(settingsData)
      ? settingsData.find(s => s.name === 'motr.NetDocument.RedirectURI')
      : null;
    if (ndRedirectSetting?.value) {
      redirectUri = ndRedirectSetting.value;
    }

    const authUrl = `${redirectUri}/NDApi/GetAuthorizationCode`;

    const width = 600;
    const height = 700;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;

    popupRef.current = window.open(
      authUrl,
      'NetDocuments Authentication',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

    if (!popupRef.current) {
      setAuthError(t('NetDocuments:PopupBlockedError', { defaultValue: 'Popup blocked. Please allow popups for this site.' }));
      setIsAuthenticating(false);
      addToast({
        type: 'error',
        title: t('NetDocuments:AuthenticationFailed', { defaultValue: 'Authentication Failed' }),
        message: t('NetDocuments:PopupBlockedMessage', { defaultValue: 'Popup blocked. Please allow popups and try again.' }),
      });
      return;
    }

    // Collect the token here, not only in the sidebar's NetDocuments icon: screens shown without
    // the sidebar (the Chrome extension / Outlook panel) would otherwise never see the sign-in.
    // The backend keeps it for /NDApi/AuthStatus (popup signed in as this user) or, when it
    // cannot tell who signed in, posts it to this window. Whichever arrives first is used.
    let finished = false;
    const finish = (tokenJson: string | null) => {
      if (finished) return;
      if (tokenJson) {
        try {
          const token = JSON.parse(tokenJson);
          // ND tokens always carry the authorization_code key.
          if (!token || !('authorization_code' in token)) return;
          storeNdToken(token as NDTokenData);
          useNetDocumentsStore.getState().setAuthenticated(true);
        } catch {
          return;
        }
      }
      finished = true;
      window.removeEventListener('message', onMessage);
      clearInterval(pollTimer);
      clearTimeout(giveUpTimer);
      setIsAuthenticating(false);
      if (popupRef.current && !popupRef.current.closed) {
        try { popupRef.current.close(); } catch { /* ignore */ }
      }
      if (!tokenJson && !getStoredNdToken()) setAuthError(t('NetDocuments:AuthCancelledOrFailed', { defaultValue: 'Authentication cancelled or failed' }));
    };
    // Only the popup itself, on the page NetDocuments returns to (redirectUri), may hand over a
    // token; any other window or frame posting a token-shaped string is ignored.
    let expectedOrigin = '';
    try { expectedOrigin = new URL(authUrl, window.location.href).origin; } catch { /* bad setting */ }
    const onMessage = (e: MessageEvent) => {
      if (e.source !== popupRef.current || e.origin !== expectedOrigin) return;
      if (typeof e.data === 'string' && e.data) finish(e.data);
    };
    const fetchStatus = async (): Promise<string | null> => {
      try {
        const { data } = await apiClient.get<{ completed?: boolean; token?: string }>('/NDApi/AuthStatus');
        return data?.completed && data.token ? data.token : null;
      } catch {
        return null;
      }
    };
    window.addEventListener('message', onMessage);

    let polling = false;
    const pollTimer = setInterval(async () => {
      if (polling || finished) return;
      polling = true;
      const closed = !!popupRef.current?.closed;
      const token = await fetchStatus();
      polling = false;
      if (token) finish(token);
      // Closed and nothing came back (one last check above): cancelled or failed.
      else if (closed) finish(null);
    }, 1500);

    // Give up after 5 minutes
    const giveUpTimer = setTimeout(() => finish(null), 5 * 60 * 1000);
  }, [settingsData, addToast, t]);

  /**
   * Log out from NetDocuments
   */
  const logout = useCallback(() => {
    clearNdToken();
    useNetDocumentsStore.getState().setAuthenticated(false);
    addToast({
      type: 'info',
      title: t('NetDocuments:Disconnected', { defaultValue: 'Disconnected' }),
      message: t('NetDocuments:DisconnectedMessage', { defaultValue: 'Disconnected from NetDocuments' }),
    });
  }, [addToast, t]);

  /**
   * Get current access token (refreshes if expired).
   * Delegates to the shared ndTokenManager.
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const token = await getValidNdAccessToken();
    if (!token) useNetDocumentsStore.getState().setAuthenticated(false);
    return token;
  }, []);

  return {
    isAuthenticated,
    isAuthenticating,
    authError,
    authenticate,
    logout,
    getAccessToken,
    refreshToken,
  };
};

// ============================================================================
// Cabinet Hooks
// ============================================================================

/**
 * Hook for fetching NetDocuments cabinets
 */
export const useNDCabinets = (enabled = true) => {
  const { isAuthenticated } = useNetDocumentsAuth();

  return useQuery({
    queryKey: netDocumentsKeys.cabinets(),
    queryFn: async () => {
      const response = await api.appDocumentsProviderGetCabinetsList({
        providerName: PROVIDER_NAME,
      });
      return (response.data || []) as MotrDocumentsModelsDocumentRepoCabinetInfoVm[];
    },
    enabled: enabled && isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/** Cabinet settings with attribute IDs from database */
export interface NDCabinetSetting {
  id: string;
  repositoryId: string | null;
  cabinetId: string | null;
  cabinetName: string | null;
  firstAttributeId: string | null;
  firstAttributeName: string | null;
  firstAttributeDisplayName: string | null;
  secondAttributeId: string | null;
  secondAttributeName: string | null;
  secondAttributeDisplayName: string | null;
  thirdAttributeId: string | null;
  thirdAttributeName: string | null;
  thirdAttributeDisplayName: string | null;
}

/**
 * Hook for fetching cabinet settings (contains attribute IDs)
 */
export const useNDCabinetSettings = (enabled = true) => {
  return useQuery({
    queryKey: netDocumentsKeys.cabinetSettings(),
    queryFn: async () => {
      const response = await api.appCabinetsExtnList({
        MaxResultCount: 1000,
      });
      return (response.data?.items || []) as NDCabinetSetting[];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for fetching folders in a cabinet
 */
export const useNDFolders = (cabinetId: string, enabled = true) => {
  const { isAuthenticated } = useNetDocumentsAuth();

  return useQuery({
    queryKey: netDocumentsKeys.folders(cabinetId),
    queryFn: async () => {
      const response = await api.appDocumentsProviderGetFoldersList({
        CabinetId: cabinetId,
        Provider: PROVIDER_NAME,
      });
      // Response is { isSuccess, result: { standardList: [...] }, message }
      // result can be a parsed object OR a JSON string depending on serialization
      const data = response.data;
      if (data?.isSuccess && data?.result) {
        const result = typeof data.result === 'string'
          ? (() => { try { return JSON.parse(data.result); } catch { return null; } })()
          : data.result;
        return (result?.standardList || []) as NDFolderItem[];
      }
      return [];
    },
    enabled: enabled && isAuthenticated && !!cabinetId,
    staleTime: 5 * 60 * 1000,
  });
};

/** Folder item from NetDocuments */
export interface NDFolderItem {
  id: string;
  name: string;
  extension?: string;
  wstype?: string;
}

/** Document item from NetDocuments */
export interface NDDocumentItem {
  id: string;
  name: string;
  extension?: string;
  created?: string;
  createdBy?: string;
  modified?: string;
  modifiedBy?: string;
  url?: string;
  envId?: string;
  version?: string;
}

/**
 * Hook for fetching workspace data (documents list) after selecting client and matter
 */
export const useNDWorkspaceData = (
  cabinetId: string,
  clientId: string,
  matterId: string,
  enabled = true
) => {
  const { isAuthenticated } = useNetDocumentsAuth();

  return useQuery({
    queryKey: netDocumentsKeys.workspaceData(cabinetId, clientId, matterId),
    queryFn: async () => {
      const response = await api.appDocumentsProviderGetWorkspaceDataList({
        CabinetId: cabinetId,
        ClientId: clientId,
        MatterId: matterId,
        Provider: PROVIDER_NAME,
      });
      // Response is { isSuccess, result: { standardList: [...] }, message }
      // result can be a parsed object OR a JSON string depending on serialization
      const data = response.data as { isSuccess?: boolean; result?: unknown; message?: string };
      if (data?.isSuccess && data?.result) {
        const result = typeof data.result === 'string'
          ? (() => { try { return JSON.parse(data.result); } catch { return null; } })()
          : data.result;
        return ((result as { standardList?: NDDocumentItem[] })?.standardList || []) as NDDocumentItem[];
      }
      return [];
    },
    enabled: enabled && isAuthenticated && !!cabinetId && !!clientId && !!matterId,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Hook for fetching clients (tier 1 attributes) for a cabinet
 * Note: Token is managed via localStorage and passed via custom header if needed
 */
export const useNDClients = (cabinetId: string, attributeId: string, enabled = true) => {
  const { isAuthenticated } = useNetDocumentsAuth();

  return useQuery({
    queryKey: netDocumentsKeys.clients(cabinetId, attributeId),
    queryFn: async () => {
      const response = await api.appDocumentsProviderGetClientsList({
        CabinetId: cabinetId,
        AttributeId: attributeId,
        Provider: PROVIDER_NAME,
      });
      return response.data || [];
    },
    enabled: enabled && isAuthenticated && !!cabinetId && !!attributeId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for fetching matters (tier 2 attributes) for a client
 */
export const useNDMatters = (
  cabinetId: string,
  clientId: string,
  attributeId: string,
  enabled = true
) => {
  const { isAuthenticated } = useNetDocumentsAuth();

  return useQuery({
    queryKey: netDocumentsKeys.matters(cabinetId, clientId, attributeId),
    queryFn: async () => {
      const response = await api.appDocumentsProviderGetMattersList({
        CabinetId: cabinetId,
        ClientId: clientId,
        AttributeId: attributeId,
        Provider: PROVIDER_NAME,
      });
      return response.data || [];
    },
    enabled: enabled && isAuthenticated && !!cabinetId && !!clientId && !!attributeId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for fetching folder content (documents and subfolders)
 */
export const useNDFolderContent = (cabinetId: string, folderId?: string, enabled = true) => {
  const { isAuthenticated } = useNetDocumentsAuth();

  return useQuery({
    queryKey: netDocumentsKeys.folderContent(cabinetId, folderId),
    queryFn: async () => {
      const response = await api.appDocumentsProviderGetFolderContentList({
        CabinetId: cabinetId,
        FolderId: folderId,
        Provider: PROVIDER_NAME,
      });
      // Response is { isSuccess, result: { standardList: [...] }, message }
      // result can be a parsed object OR a JSON string depending on serialization
      const data = response.data as { isSuccess?: boolean; result?: unknown; message?: string };
      if (data?.isSuccess && data?.result) {
        const result = typeof data.result === 'string'
          ? (() => { try { return JSON.parse(data.result); } catch { return null; } })()
          : data.result;
        return ((result as { standardList?: NDDocumentItem[] })?.standardList || []) as NDDocumentItem[];
      }
      return [];
    },
    enabled: enabled && isAuthenticated && !!cabinetId && !!folderId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook for fetching workspace data
 */
export const useNDWorkspace = (
  cabinetId: string,
  clientId?: string,
  matterId?: string,
  enabled = true
) => {
  const { isAuthenticated } = useNetDocumentsAuth();

  return useQuery({
    queryKey: netDocumentsKeys.workspace(cabinetId, clientId, matterId),
    queryFn: async () => {
      const response = await api.appDocumentsProviderGetWorkspaceDataList({
        CabinetId: cabinetId,
        ClientId: clientId,
        MatterId: matterId,
        Provider: PROVIDER_NAME,
      });
      return response.data;
    },
    enabled: enabled && isAuthenticated && !!cabinetId,
    staleTime: 5 * 60 * 1000,
  });
};

// ============================================================================
// Upload Location Hooks
// ============================================================================

/** Upload location item - properties match NetDocuments API response (Pascal case) */
export interface NDUploadLocation {
  Name: string;
  Id: string;
  EnvId?: string;
  /** Some ND API versions return lowercase envId */
  envId?: string;
}

/**
 * The upload locations (folders) of a client/matter workspace in NetDocuments; [] when the
 * workspace does not exist (yet) or cannot be read. Used by useNDUploadLocations and by the
 * Start New Job wizard to file email attachments once it has created a new matter.
 */
export const fetchNDUploadLocations = async (
  cabinetId: string,
  clientId: string,
  matterId: string,
): Promise<NDUploadLocation[]> => {
  // First get the workspace to get the workspaceId
  const workspaceResponse = await api.appDocumentsProviderGetWorkspaceList({
    CabinetId: cabinetId,
    ClientId: clientId,
    MatterId: matterId,
    Provider: PROVIDER_NAME,
  });

  const workspaceData = workspaceResponse.data as { isSuccess?: boolean; result?: string; message?: string };
  if (!workspaceData?.isSuccess || !workspaceData?.result) {
    return [];
  }

  const workspace = JSON.parse(workspaceData.result);
  const workspaceId = workspace?.standardAttributes?.id;
  if (!workspaceId) {
    return [];
  }

  // Then get the locations for that workspace
  const locationResponse = await api.appDocumentsProviderGetLocationsList({
    CabinetId: cabinetId,
    WorkspaceId: workspaceId,
    Provider: PROVIDER_NAME,
  });

  const locationData = locationResponse.data as { isSuccess?: boolean; result?: unknown; message?: string };
  if (!locationData?.isSuccess || !locationData?.result) {
    return [];
  }

  // Result is an array of arrays, flatten it
  const parsed = typeof locationData.result === 'string'
    ? JSON.parse(locationData.result)
    : locationData.result;
  const locations: NDUploadLocation[] = [];
  if (Array.isArray(parsed)) {
    parsed.forEach((group) => {
      if (Array.isArray(group)) {
        group.forEach((item: Record<string, unknown>) => {
          // ABP/ASP.NET serialization can return either PascalCase or
          // camelCase depending on the endpoint and environment.
          const name = String(item.Name ?? item.name ?? '');
          const id = String(item.Id ?? item.id ?? '');
          const envId = String(item.EnvId ?? item.envId ?? '');
          if (!name || (!envId && !id)) return;
          locations.push({
            Name: name,
            Id: id,
            EnvId: envId || undefined,
            envId: envId || undefined,
          });
        });
      }
    });
  }

  // Sort by name
  locations.sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
  return locations;
};

/**
 * Hook for fetching upload locations within a workspace
 * Used when uploading files directly to NetDocuments
 */
export const useNDUploadLocations = (
  cabinetId: string,
  clientId: string,
  matterId: string,
  enabled = true
) => {
  const { isAuthenticated } = useNetDocumentsAuth();

  return useQuery({
    queryKey: [...netDocumentsKeys.all, 'uploadLocations', cabinetId, clientId, matterId] as const,
    queryFn: () => fetchNDUploadLocations(cabinetId, clientId, matterId),
    enabled: enabled && isAuthenticated && !!cabinetId && !!clientId && !!matterId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for uploading files directly to NetDocuments
 */
export const useNDUploadFile = () => {
  const { addToast } = useNotificationStore();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      file,
      cabinetId,
      destination,
      jobId,
    }: {
      file: File;
      cabinetId: string;
      destination?: string;
      jobId?: string;
    }) => {
      const formData = new FormData();
      formData.append('files', file);
      formData.append('cabinetId', cabinetId);
      formData.append('provider', PROVIDER_NAME);
      if (destination) {
        formData.append('destination', destination);
      }
      if (jobId) {
        formData.append('jobId', jobId);
      }

      // Use the raw API client for multipart upload
      // Route: AppController.UploadDocument (MVC controller, no /api prefix)
      // Matches Angular's restService.request({ url: `/app/UploadDocument` })
      const response = await apiClient.post('/App/UploadDocument', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = response.data as {
        isSuccess?: boolean;
        message?: string;
        result?: {
          documents: Array<{
            fileName: string;
            url: string;
            documentRepoId: string;
          }>;
        };
      };

      if (!data?.isSuccess) {
        throw new Error(data?.message || 'Upload failed');
      }

      return data.result?.documents || [];
    },
    onError: (error) => {
      addToast({
        type: 'error',
        title: t('NetDocuments:UploadFailed', { defaultValue: 'Upload Failed' }),
        message: error instanceof Error ? error.message : t('NetDocuments:FailedToUploadFile', { defaultValue: 'Failed to upload file' }),
      });
    },
  });
};

// ============================================================================
// Document Action Hooks
// ============================================================================

/**
 * Input parameters for getting a secure link
 */
export interface GetSecureLinkParams {
  documentId: string;
  repoDocId?: string | null;
  cabinetId?: string | null;
  expirationDate?: string | null;
  download?: boolean;
  notifyMe?: boolean;
}

/**
 * Hook for generating secure document links
 */
export const useGetSecureLink = () => {
  const { addToast } = useNotificationStore();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (params: GetSecureLinkParams): Promise<string> => {
      const requestData: MotrDocumentsGetProviderSecureLinkVm = {
        id: params.documentId,
        documentRepositoryId: params.repoDocId || undefined,
        cabinetId: params.cabinetId || undefined,
        expirationDate: params.expirationDate || undefined,
        download: params.download ?? false,
        notifyMe: params.notifyMe ?? false,
      };

      const response = await api.appDocumentsProviderGetSecureLinkCreate(requestData);

      const link = response.data;
      if (!link) {
        throw new Error('Failed to generate secure link');
      }

      return link;
    },
    onSuccess: async (link) => {
      try {
        await navigator.clipboard.writeText(link);
        addToast({
          type: 'success',
          title: t('NetDocuments:LinkCopied', { defaultValue: 'Link Copied' }),
          message: t('NetDocuments:LinkCopiedMessage', { defaultValue: 'Secure link has been copied to clipboard' }),
        });
      } catch {
        addToast({
          type: 'info',
          title: t('NetDocuments:SecureLinkGenerated', { defaultValue: 'Secure Link Generated' }),
          message: link,
          duration: 10000,
        });
      }
    },
    onError: (error) => {
      addToast({
        type: 'error',
        title: t('NetDocuments:FailedToGenerateLink', { defaultValue: 'Failed to Generate Link' }),
        message: error instanceof Error ? error.message : t('Calendar:UnexpectedError', { defaultValue: 'An unexpected error occurred' }),
      });
    },
  });
};

/**
 * Input parameters for following documents
 */
export interface FollowDocumentsParams {
  jobId: string;
  stepId: string;
  documents: MotrDocumentsModelsFollowDocumentViewModel[];
  recipients: string[];
  provider?: string | null;
  cabinetId?: string | null;
  repositoryId?: string | null;
}

/**
 * Hook for following/sharing documents with recipients
 */
export const useFollowDocuments = () => {
  const { addToast } = useNotificationStore();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (params: FollowDocumentsParams) => {
      const requestData: MotrDocumentsModelsFollowDocumentsVm = {
        jobId: params.jobId,
        stepId: params.stepId,
        documents: params.documents,
        recipients: params.recipients,
        provider: params.provider || undefined,
        cabinetId: params.cabinetId || undefined,
        repositoryId: params.repositoryId || undefined,
      };

      const response = await api.appDocumentsProviderFollowDocumentsCreate(requestData);
      return response.data;
    },
    onSuccess: () => {
      addToast({
        type: 'success',
        title: t('NetDocuments:DocumentsShared', { defaultValue: 'Documents Shared' }),
        message: t('NetDocuments:DocumentsSharedMessage', { defaultValue: 'Follow notifications have been sent to the selected recipients' }),
      });
    },
    onError: (error) => {
      addToast({
        type: 'error',
        title: t('NetDocuments:FailedToShareDocuments', { defaultValue: 'Failed to Share Documents' }),
        message: error instanceof Error ? error.message : t('Calendar:UnexpectedError', { defaultValue: 'An unexpected error occurred' }),
      });
    },
  });
};

/**
 * Input parameters for navigating to workspace
 */
export interface GoToWorkspaceParams {
  cabinetId?: string | null;
  clientId?: string | null;
  matterId?: string | null;
  provider?: string | null;
}

/**
 * Hook for navigating to document workspace in NetDocuments
 */
export const useGoToWorkspace = () => {
  const { addToast } = useNotificationStore();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (params: GoToWorkspaceParams) => {
      const response = await api.appDocumentsProviderGetWorkspaceList({
        CabinetId: params.cabinetId || undefined,
        ClientId: params.clientId || undefined,
        MatterId: params.matterId || undefined,
        Provider: params.provider || PROVIDER_NAME,
      });

      const result = response.data;
      if (!result?.isSuccess || !result.result) {
        throw new Error(result?.message || 'Failed to retrieve workspace information');
      }

      return result.result;
    },
    onSuccess: (resultJson) => {
      try {
        const parsed = JSON.parse(resultJson);
        const envId = parsed?.standardAttributes?.envId;

        if (envId) {
          const encodedEnvId = envId.replace(/\^/g, '%5E');
          const url = `https://vault.netvoyage.com/neWeb2/workspace/${encodedEnvId}/docs/summary`;
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          addToast({
            type: 'warning',
            title: t('NetDocuments:WorkspaceNotFound', { defaultValue: 'Workspace Not Found' }),
            message: t('NetDocuments:WorkspaceUrlNotDetermined', { defaultValue: 'Unable to determine workspace URL from response' }),
          });
        }
      } catch {
        addToast({
          type: 'warning',
          title: t('NetDocuments:WorkspaceNotFound', { defaultValue: 'Workspace Not Found' }),
          message: t('NetDocuments:WorkspaceResponseParseFailed', { defaultValue: 'Unable to parse workspace response' }),
        });
      }
    },
    onError: (error) => {
      addToast({
        type: 'error',
        title: t('NetDocuments:FailedToOpenWorkspace', { defaultValue: 'Failed to Open Workspace' }),
        message: error instanceof Error ? error.message : t('Calendar:UnexpectedError', { defaultValue: 'An unexpected error occurred' }),
      });
    },
  });
};

/**
 * Input parameters for viewing document in browser
 */
export interface ViewInBrowserParams {
  documentId: string;
  getSecureLink?: boolean;
}

/**
 * Hook for viewing a document in the browser
 */
export const useViewInBrowser = () => {
  const { addToast } = useNotificationStore();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (params: ViewInBrowserParams) => {
      const response = await api.appDocumentsProviderViewDocumentList({
        DocumentId: params.documentId,
        ForceLinkOnly: true,
        GetSecureLink: params.getSecureLink ?? false,
      });

      const result = response.data?.result;
      if (!result?.url) {
        throw new Error('Failed to retrieve document view URL');
      }

      return result;
    },
    onSuccess: (result) => {
      if (result.url) {
        window.open(result.url, '_blank', 'noopener,noreferrer');
      }
    },
    onError: (error) => {
      addToast({
        type: 'error',
        title: t('NetDocuments:FailedToViewDocument', { defaultValue: 'Failed to View Document' }),
        message: error instanceof Error ? error.message : t('Calendar:UnexpectedError', { defaultValue: 'An unexpected error occurred' }),
      });
    },
  });
};

/**
 * Hook for validating user token with provider
 */
export const useValidateNDUser = () => {
  const { getAccessToken } = useNetDocumentsAuth();

  return useMutation({
    mutationFn: async () => {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await api.appDocumentsProviderGetUserDetailsList({
        token,
        provider: PROVIDER_NAME,
      });

      return response.data;
    },
  });
};

// ============================================================================
// Combined Hook
// ============================================================================

/**
 * Combined hook providing all NetDocuments integration functionality
 */
export const useNetDocuments = () => {
  const auth = useNetDocumentsAuth();
  const cabinetsQuery = useNDCabinets(auth.isAuthenticated);
  const getSecureLinkMutation = useGetSecureLink();
  const followDocumentsMutation = useFollowDocuments();
  const goToWorkspaceMutation = useGoToWorkspace();
  const viewInBrowserMutation = useViewInBrowser();

  return {
    // Authentication
    isAuthenticated: auth.isAuthenticated,
    isAuthenticating: auth.isAuthenticating,
    authError: auth.authError,
    authenticate: auth.authenticate,
    logout: auth.logout,
    getAccessToken: auth.getAccessToken,

    // Cabinets
    cabinets: cabinetsQuery.data || [],
    isLoadingCabinets: cabinetsQuery.isLoading,
    cabinetsError: cabinetsQuery.error,

    // Get secure link
    getSecureLink: getSecureLinkMutation.mutateAsync,
    isGettingSecureLink: getSecureLinkMutation.isPending,
    getSecureLinkError: getSecureLinkMutation.error,

    // Follow documents
    followDocuments: followDocumentsMutation.mutateAsync,
    isFollowingDocuments: followDocumentsMutation.isPending,
    followDocumentsError: followDocumentsMutation.error,

    // Go to workspace
    goToWorkspace: goToWorkspaceMutation.mutateAsync,
    isGoingToWorkspace: goToWorkspaceMutation.isPending,
    goToWorkspaceError: goToWorkspaceMutation.error,

    // View in browser
    viewInBrowser: viewInBrowserMutation.mutateAsync,
    isViewingInBrowser: viewInBrowserMutation.isPending,
    viewInBrowserError: viewInBrowserMutation.error,

    // Combined loading state
    isLoading:
      auth.isAuthenticating ||
      cabinetsQuery.isLoading ||
      getSecureLinkMutation.isPending ||
      followDocumentsMutation.isPending ||
      goToWorkspaceMutation.isPending ||
      viewInBrowserMutation.isPending,
  };
};

export default useNetDocuments;
